/**
 * Stage 24 · Scenario Lab. Model "what if?" without touching production data.
 */
import { orgLabel } from "@/lib/orgLabel";
import { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FlaskConical, Plus, Play, Lock, GitCompare, AlertTriangle, Rocket } from "lucide-react";

const KINDS = [
  { code: "device_refresh", label: "Device Refresh" },
  { code: "new_office", label: "New Office" },
  { code: "office_closure", label: "Office Closure" },
  { code: "mass_onboarding", label: "Mass Onboarding" },
  { code: "acquisition", label: "Acquisition" },
  { code: "standardisation", label: "Standardisation" },
  { code: "itad_programme", label: "ITAD Programme" },
  { code: "custom", label: "Custom" },
];

export default function ScenarioLab() {
  const [items, setItems] = useState([]);
  const [masters, setMasters] = useState([]);
  const [mc, setMc] = useState("");
  const [me, setMe] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [compareIds, setCompareIds] = useState([]);
  const [compareData, setCompareData] = useState(null);
  const toggleCompare = (id) => setCompareIds((v) => v.includes(id) ? v.filter((x) => x !== id) : (v.length < 3 ? [...v, id] : v));
  const runCompare = async () => {
    try { const { data } = await api.post("/scenarios/compare", { scenario_ids: compareIds }); setCompareData(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => { setMe(data); if (data?.master_company_id) setMc(data.master_company_id); });
    api.get("/master-companies").then(({ data }) => { setMasters(data.items || []); setMc(current => current || data.items?.[0]?.id || ""); });
  }, []);

  const isSuntronix = me && !me.master_company_id;
  const load = useCallback(() => mc && api.get("/scenarios/", { params: { master_company_id: mc } }).then(({ data }) => setItems(data.items || [])), [mc]);
  useEffect(() => { load(); }, [load, mc]);

  return (
    <div className="max-w-6xl space-y-5" data-testid="scenario-lab">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Insights · Stage 24</div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">Scenario Lab</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Model future decisions against your authorised estate. Every scenario freezes an input snapshot — production records are never changed.
        </p>
      </div>

      <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2 text-xs inline-flex items-center gap-2">
        <FlaskConical className="h-4 w-4" /> Simulation · No production records will be changed.
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {isSuntronix && (
          <select className="input min-w-[240px]" value={mc} onChange={(e) => setMc(e.target.value)} data-testid="scn-tenant">
            {masters.map((m) => <option key={m.id} value={m.id}>{orgLabel(m, masters)}</option>)}
          </select>
        )}
        <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white inline-flex items-center gap-1" data-testid="scn-new">
          <Plus className="h-4 w-4" /> New scenario
        </button>
        {compareIds.length >= 2 && (
          <button onClick={runCompare} className="h-9 px-3 text-sm rounded-md border border-slate-900 text-slate-900 inline-flex items-center gap-1" data-testid="scn-compare-run">
            <GitCompare className="h-4 w-4" /> Compare {compareIds.length} plans
          </button>
        )}
      </div>
      {compareData && <CompareTable items={compareData.items} onClose={() => setCompareData(null)} />}

      {showForm && <ScenarioForm mc={mc} onCancel={() => setShowForm(false)} onCreated={(s) => { setShowForm(false); load(); setSelected(s); }} />}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-1 bg-white border border-slate-200 rounded-md divide-y">
          {items.length === 0 ? (
            <div className="p-6 text-sm text-slate-500">No scenarios yet.</div>
          ) : items.map((s) => (
            <div key={s.id} className={"flex gap-2 p-3 hover:bg-slate-50 " + (selected?.id === s.id ? "bg-slate-50" : "")}>
              <input type="checkbox" checked={compareIds.includes(s.id)} onChange={() => toggleCompare(s.id)}
                className="mt-1" data-testid={`scn-compare-${s.scenario_number}`} title="Add to compare (up to 3)" />
              <button onClick={() => setSelected(s)} className="flex-1 text-left" data-testid={`scn-row-${s.scenario_number}`}>
                <div className="text-sm font-mono text-slate-900">{s.scenario_number}</div>
                <div className="text-xs text-slate-700 mt-0.5">{s.name}</div>
                <div className="text-[10px] text-slate-500 mt-1 uppercase tracking-widest">{s.kind?.replace(/_/g, " ")} · {s.status}</div>
              </button>
            </div>
          ))}
        </div>
        <div className="md:col-span-2">
          {selected ? <ScenarioDetail scenario={selected} isSuntronix={isSuntronix} onChanged={load} /> : (
            <div className="bg-white border border-slate-200 rounded-md p-8 text-sm text-slate-500 text-center">Create or select a scenario on the left.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function ScenarioForm({ mc, onCancel, onCreated }) {
  const [f, setF] = useState({ kind: "device_refresh", name: "Refresh MacBooks > 48 months", description: "", assumptions: { refresh_age_months: 48, replacement_price_inr: 200000, reuse_rate: 0.2, procurement_lead_days: 30, deployment_capacity_per_day: 40 } });
  const setAsm = (k, v) => setF((s) => ({ ...s, assumptions: { ...s.assumptions, [k]: v } }));
  const save = async () => {
    try {
      const { data } = await api.post("/scenarios/", { master_company_id: mc, ...f });
      toast.success(`${data.scenario_number} created`); onCreated(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2" data-testid="scn-form">
      <div className="grid grid-cols-2 gap-2">
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Kind</div>
          <select className="input" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="scn-kind">
            {KINDS.map((k) => <option key={k.code} value={k.code}>{k.label}</option>)}
          </select></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Name</div>
          <input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="scn-name" /></label>
        <label className="col-span-2"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Description</div>
          <input className="input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></label>
      </div>
      {f.kind === "device_refresh" && (
        <div className="grid grid-cols-3 gap-2">
          <NumInput label="Refresh age (mo)" v={f.assumptions.refresh_age_months} onChange={(v) => setAsm("refresh_age_months", v)} />
          <NumInput label="Price INR" v={f.assumptions.replacement_price_inr} onChange={(v) => setAsm("replacement_price_inr", v)} />
          <NumInput label="Reuse rate" v={f.assumptions.reuse_rate} onChange={(v) => setAsm("reuse_rate", v)} step="0.05" />
          <NumInput label="Lead (days)" v={f.assumptions.procurement_lead_days} onChange={(v) => setAsm("procurement_lead_days", v)} />
          <NumInput label="Capacity/day" v={f.assumptions.deployment_capacity_per_day} onChange={(v) => setAsm("deployment_capacity_per_day", v)} />
        </div>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <button className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white" onClick={onCancel}>Cancel</button>
        <button className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white inline-flex items-center gap-1" onClick={save} data-testid="scn-save"><Play className="h-4 w-4" /> Simulate</button>
      </div>
    </div>
  );
}
function NumInput({ label, v, onChange, step }) {
  return (
    <label>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <input type="number" className="input" value={v ?? ""} step={step || "1"} onChange={(e) => onChange(Number(e.target.value))} />
    </label>
  );
}

function ScenarioDetail({ scenario, isSuntronix, onChanged }) {
  const r = scenario.result || {};
  const snap = scenario.snapshot || {};
  const approve = async () => {
    try { await api.post(`/scenarios/${scenario.id}/approve`); toast.success("Approved — snapshot frozen"); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3" data-testid="scn-detail">
      <div className="bg-white border border-slate-200 rounded-md p-4">
        <div className="flex items-start gap-2">
          <div>
            <div className="text-sm font-mono text-slate-900">{scenario.scenario_number}</div>
            <div className="text-lg font-heading font-semibold text-slate-900 mt-0.5">{scenario.name}</div>
            <div className="text-xs text-slate-500 mt-1">{scenario.kind?.replace(/_/g, " ")} · {scenario.status}
              {scenario.status === "approved" && <span className="ml-2 inline-flex items-center gap-1 text-emerald-700"><Lock className="h-3 w-3" /> Immutable snapshot</span>}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {isSuntronix && scenario.status !== "approved" && (
              <button onClick={approve} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white inline-flex items-center gap-1" data-testid="scn-approve">
                <Lock className="h-4 w-4" /> Approve
              </button>
            )}
            {isSuntronix && scenario.status === "approved" && (
              <ConvertButton scenarioId={scenario.id} onDone={onChanged} />
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4">
        <h3 className="text-sm font-medium text-slate-900 mb-2">Estate snapshot</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Tile label="Assets" value={snap.totals?.assets} />
          <Tile label="Employees" value={snap.totals?.employees} />
          <Tile label="Active orders" value={snap.totals?.active_orders} />
          <Tile label="Data quality" value={snap.data_quality?.confidence} sub={`${snap.data_quality?.assets_with_purchase_date_pct ?? 0}% w/ purchase date`} />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4">
        <h3 className="text-sm font-medium text-slate-900 mb-2">Result <span className="text-[10px] text-slate-500 uppercase ml-1">estimate · forecast</span></h3>
        {scenario.kind === "device_refresh" ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <Tile label="Eligible devices" value={r.eligible_devices} />
            <Tile label="Reusable from recoveries" value={r.reusable_from_recoveries} />
            <Tile label="To procure" value={r.to_procure} />
            <Tile label="Programme cost" value={fmtINR(r.programme_cost_inr_estimate)} />
            <Tile label="Recovery value" value={fmtINR(r.recovery_value_inr_estimate)} tone="ok" />
            <Tile label="Net cost" value={fmtINR(r.net_cost_inr_estimate)} tone="warn" />
            <Tile label="Expected duration" value={r.expected_duration_days != null ? `${r.expected_duration_days} days` : "—"} />
            <Tile label="Countries" value={(r.countries || []).join(", ") || "—"} />
            <Tile label="Capacity risk" value={r.capacity_risk ? "YES" : "no"} tone={r.capacity_risk ? "warn" : undefined} />
          </div>
        ) : (
          <pre className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto">{JSON.stringify(r, null, 2)}</pre>
        )}
        {(r.risks || []).length > 0 && (
          <div className="mt-3 space-y-1">
            {r.risks.map((rk, i) => (
              <div key={i} className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" /> {rk}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value, sub, tone }) {
  const cls = tone === "ok" ? "text-emerald-700" : tone === "warn" ? "text-amber-700" : "text-slate-900";
  return (
    <div className="rounded-md border border-slate-200 p-3">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={"mt-1 text-lg font-heading font-semibold tabular-nums " + cls}>{value ?? "—"}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
function fmtINR(n) { return n == null ? "—" : "₹" + Number(n).toLocaleString("en-IN"); }

function CompareTable({ items, onClose }) {
  const rows = [
    { key: "eligible_devices", label: "Eligible devices" },
    { key: "to_procure", label: "To procure" },
    { key: "programme_cost_inr_estimate", label: "Programme cost", money: true },
    { key: "recovery_value_inr_estimate", label: "Recovery value", money: true },
    { key: "net_cost_inr_estimate", label: "Net cost", money: true },
    { key: "expected_duration_days", label: "Duration (days)" },
    { key: "capacity_risk", label: "Capacity risk" },
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="scn-compare-table">
      <div className="p-3 border-b border-slate-200 flex items-center gap-2">
        <GitCompare className="h-4 w-4 text-slate-500" />
        <h3 className="text-sm font-medium text-slate-900">Compare plans</h3>
        <button onClick={onClose} className="ml-auto text-xs text-slate-500 hover:text-slate-900">Close</button>
      </div>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
          <tr><th className="text-left py-2 px-3">Metric</th>{items.map((it) => <th key={it.id} className="text-right py-2 px-3">{it.number || it.name}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-t border-slate-100">
              <td className="py-2 px-3 text-slate-700">{r.label}</td>
              {items.map((it) => {
                const v = (it.result || {})[r.key];
                return <td key={it.id} className="py-2 px-3 text-right tabular-nums text-slate-900">
                  {v == null ? "—" : r.money ? fmtINR(v) : String(v)}
                </td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ConvertButton({ scenarioId, onDone }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!window.confirm("Convert this approved scenario into a programme? This will create a programme with country waves and a procurement forecast — no live orders will be created.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/scenarios/${scenarioId}/convert-to-programme`);
      toast.success(`${data.programme_number} created · ${data.waves} waves · forecast: ${data.forecast ? "yes" : "no"}`);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <button onClick={go} disabled={busy}
      className="h-9 px-3 text-sm rounded-md bg-emerald-700 text-white inline-flex items-center gap-1 disabled:opacity-40"
      data-testid="scn-convert">
      <Rocket className="h-4 w-4" /> Convert to programme
    </button>
  );
}
